import React, { useRef, useState } from 'react'
import { cn } from '../../lib/utils'

/**
 * LyricBlockEditor Component
 * Visual representation of a single lyric on the timeline
 * Supports selection, dragging, and resizing
 */
const LyricBlockEditor = ({
  lyric,
  index,
  isSelected,
  isCurrent,
  isPast,
  timeToPixels,
  pixelsToTime,
  zoom,
  onSelect,
  onDoubleClick,
  onDragStart,
  onDrag,
  onDragEnd,
  onResizeStart,
  onResize,
  onResizeEnd,
  snapTime
}) => {
  const blockRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeHandle, setResizeHandle] = useState(null) // 'left' or 'right'

  const startX = timeToPixels(lyric.start_time, zoom)
  const endX = timeToPixels(lyric.end_time, zoom)
  const width = endX - startX
  const minWidth = 30 // Minimum block width in pixels

  // Handle mouse down for dragging the block
  const handleMouseDown = (e) => {
    if (e.button !== 0) return // Only left click
    if (e.target.dataset.handle) return // Ignore if clicking resize handle

    e.preventDefault()
    e.stopPropagation()

    onSelect(index)
    setIsDragging(true)

    const startPageX = e.pageX
    const startTime = lyric.start_time
    const duration = lyric.end_time - lyric.start_time

    if (onDragStart) onDragStart(index)

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startPageX
      const deltaTime = pixelsToTime(deltaX, zoom)
      let newStartTime = startTime + deltaTime

      // Apply snapping
      newStartTime = snapTime ? snapTime(newStartTime) : newStartTime

      // Ensure within bounds
      newStartTime = Math.max(0, newStartTime)

      const newEndTime = newStartTime + duration

      if (onDrag) {
        onDrag(index, {
          start_time: newStartTime,
          end_time: newEndTime
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      if (onDragEnd) onDragEnd(index)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Handle mouse down for resizing
  const handleResizeMouseDown = (e, handle) => {
    if (e.button !== 0) return

    e.preventDefault()
    e.stopPropagation()

    onSelect(index)
    setIsResizing(true)
    setResizeHandle(handle)

    const startPageX = e.pageX
    const originalStartTime = lyric.start_time
    const originalEndTime = lyric.end_time

    if (onResizeStart) onResizeStart(index, handle)

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startPageX
      const deltaTime = pixelsToTime(deltaX, zoom)

      let newStartTime = originalStartTime
      let newEndTime = originalEndTime

      if (handle === 'left') {
        newStartTime = originalStartTime + deltaTime
        newStartTime = snapTime ? snapTime(newStartTime) : newStartTime
        newStartTime = Math.max(0, newStartTime)

        // Ensure minimum duration
        const minDuration = pixelsToTime(minWidth, zoom)
        if (newEndTime - newStartTime < minDuration) {
          newStartTime = newEndTime - minDuration
        }
      } else if (handle === 'right') {
        newEndTime = originalEndTime + deltaTime
        newEndTime = snapTime ? snapTime(newEndTime) : newEndTime

        // Ensure minimum duration
        const minDuration = pixelsToTime(minWidth, zoom)
        if (newEndTime - newStartTime < minDuration) {
          newEndTime = newStartTime + minDuration
        }
      }

      if (onResize) {
        onResize(index, {
          start_time: newStartTime,
          end_time: newEndTime
        })
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setResizeHandle(null)
      if (onResizeEnd) onResizeEnd(index)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Handle double click to edit
  const handleDoubleClick = () => {
    if (onDoubleClick) {
      onDoubleClick(index)
    }
  }

  // Truncate text for display
  const truncateText = (text, maxLength = 50) => {
    if (!text) return ''
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  return (
    <div
      ref={blockRef}
      className={cn(
        'absolute top-0 h-14 rounded-md cursor-move select-none transition-all',
        'border-2 overflow-hidden',
        isSelected
          ? 'border-blue-500 shadow-lg shadow-blue-500/50 z-20'
          : isCurrent
          ? 'border-white shadow-md z-10'
          : isPast
          ? 'border-white/40'
          : 'border-white/30',
        isDragging && 'opacity-70 scale-105',
        isResizing && 'opacity-80'
      )}
      style={{
        left: `${startX}px`,
        width: `${Math.max(width, minWidth)}px`,
        background: isSelected
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(37, 99, 235, 0.4))'
          : isCurrent
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))'
          : isPast
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.1))'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.15))',
        backdropFilter: 'blur(8px)'
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Left resize handle */}
      <div
        data-handle="left"
        className={cn(
          'absolute left-0 top-0 h-full w-2 cursor-ew-resize bg-blue-500/0 hover:bg-blue-500/50 transition-colors',
          isResizing && resizeHandle === 'left' && 'bg-blue-500'
        )}
        onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
      />

      {/* Content */}
      <div className="flex flex-col justify-center h-full px-3 pointer-events-none">
        <div className="text-white text-sm font-medium leading-tight">
          {truncateText(lyric.text)}
        </div>
        <div className="text-white/60 text-xs mt-0.5">
          {lyric.start_time.toFixed(2)}s - {lyric.end_time.toFixed(2)}s
        </div>
      </div>

      {/* Right resize handle */}
      <div
        data-handle="right"
        className={cn(
          'absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-blue-500/0 hover:bg-blue-500/50 transition-colors',
          isResizing && resizeHandle === 'right' && 'bg-blue-500'
        )}
        onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
      />
    </div>
  )
}

export default LyricBlockEditor
